import { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { LayoutDashboard, Users, Files, ListChecks, MessageSquare, CreditCard, CalendarDays, Activity, ChartNoAxesCombined, Settings, PenLine, Plug, Contact, FileText, Menu, X, LogOut, ArrowUpRight } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import Brand from './Brand'

const CpaNav = [
  ['/dashboard','Overview',LayoutDashboard], ['/clients','Clients',Users],
  ['/documents','Documents',Files], ['/reminders','Requests',ListChecks],
  ['/messages','Messages',MessageSquare], ['/invoices','Payments',CreditCard],
  ['/calendar','Tasks',CalendarDays], ['/audit-log','Activity',Activity],
  ['/reports','Reports',ChartNoAxesCombined], ['/settings','Settings',Settings],
]
const FirmTools = [
  ['/engagements','Engagements & signatures',PenLine], ['/organizer','Tax organizers',ListChecks],
  ['/contacts','Contacts',Contact], ['/integrations','Integrations',Plug],
  ['/payment-plans','Payment plans',CreditCard], ['/recurring-invoices','Recurring invoices',CreditCard],
  ['/revenue','Revenue',ChartNoAxesCombined], ['/year-over-year','Year-over-year',ChartNoAxesCombined],
  ['/proposal-builder','Proposals',FileText], ['/drake-export','Drake export',Files],
  ['/doc-analyzer','Document analyzer',FileText], ['/engagement-letter','Letter builder',PenLine],
]
const ClientNav = [
  ['/dashboard','Overview',LayoutDashboard], ['/my-documents','Documents',Files],
  ['/organizer','Tax organizer',ListChecks], ['/my-messages','Messages',MessageSquare],
  ['/my-invoices','Payments',CreditCard], ['/engagement-signing','Engagement letter',PenLine],
  ['/sign-documents','Sign documents',PenLine],
]

export default function Layout({ children }) {
  const { profile, user, signOut, isCPA } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [signingOut, setSigningOut] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [pendingSignCount, setPendingSignCount] = useState(0)
  const menuRef = useRef(null)
  const sidebarRef = useRef(null)
  useEffect(() => {
    if (!isCPA && (profile?.id || user?.id)) {
      supabase.from('clients').select('id').eq('portal_user_id', profile?.id || user?.id).maybeSingle()
        .then(({ data: client }) => {
          if (!client) return
          supabase.from('documents')
            .select('id', { count: 'exact', head: true })
            .eq('client_id', client.id)
            .eq('requires_signature', true)
            .in('signing_status', ['pending', 'partial'])
            .then(({ count }) => setPendingSignCount(count || 0))
        })
    }
  }, [isCPA, profile, user])


  useEffect(() => { setMobileMenuOpen(false) }, [location.pathname])
  useEffect(() => {
    if (!mobileMenuOpen) return
    const sidebar = sidebarRef.current
    const items = sidebar.querySelectorAll('a,button,summary')
    items[0]?.focus()
    function keydown(event) {
      if (event.key === 'Escape') { setMobileMenuOpen(false); menuRef.current?.focus() }
      if (event.key === 'Tab') {
        const visible = [...items].filter(el => el.getClientRects().length)
        if (event.shiftKey && document.activeElement === visible[0]) { event.preventDefault(); visible[visible.length - 1]?.focus() }
        else if (!event.shiftKey && document.activeElement === visible[visible.length - 1]) { event.preventDefault(); visible[0]?.focus() }
      }
    }
    sidebar.addEventListener('keydown', keydown)
    return () => sidebar.removeEventListener('keydown', keydown)
  }, [mobileMenuOpen])

  const nav = isCPA ? CpaNav : ClientNav
  const allNav = isCPA ? [...CpaNav, ...FirmTools] : ClientNav
  const title = allNav.find(([path]) => location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path + '/')))?.[1] || (location.pathname === '/billing' ? 'Payments' : 'ClearFile')
  const initials = (profile?.full_name || 'Your account').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase()
  async function handleSignOut() {
    setSigningOut(true)
    await signOut()
    navigate('/login')
  }
  function navItem([path, label, Icon]) {
    return <NavLink key={path} to={path} end={path === '/dashboard'} className={({ isActive }) => `cf-nav-link${isActive || (path === '/invoices' && location.pathname === '/billing') ? ' active' : ''}`}><Icon size={18}/><span>{label}</span>{path === '/sign-documents' && pendingSignCount > 0 && <span className="cf-nav-badge">{pendingSignCount}</span>}</NavLink>
  }
  return <div className="cf-app">
    <a className="cf-shell-skip" href="#workspace-main">Skip to workspace</a>
    {mobileMenuOpen && <button className="cf-sidebar-overlay" aria-label="Close navigation" onClick={() => { setMobileMenuOpen(false); menuRef.current?.focus() }}/ >}
    <aside ref={sidebarRef} id="workspace-navigation" className={`cf-sidebar${mobileMenuOpen ? ' open' : ''}`}>
      <button className="cf-sidebar-close" aria-label="Close navigation" onClick={() => { setMobileMenuOpen(false); menuRef.current?.focus() }}><X size={20}/></button>
      <div className="cf-sidebar-brand"><Link to="/dashboard" aria-label="ClearFile overview"><Brand light/></Link><p>{isCPA ? 'Firm workspace' : 'Your client portal'}</p></div>
      <nav aria-label="Workspace navigation">{nav.map(navItem)}{isCPA && <details open={FirmTools.some(([path]) => location.pathname === path) || undefined}><summary>More workspace tools</summary>{FirmTools.map(navItem)}</details>}</nav>
      <div className="cf-sidebar-account"><span className="cf-avatar">{initials}</span><div><strong>{profile?.full_name || 'Your account'}</strong><small>{isCPA ? 'Firm account' : 'Client account'}</small></div><button onClick={handleSignOut} disabled={signingOut}><LogOut size={15}/>{signingOut ? 'Signing out…' : 'Sign out'}</button></div>
    </aside>
    <div className="cf-workspace"><header className="cf-workspace-header"><button ref={menuRef} className="cf-menu-button" aria-label="Open navigation" aria-expanded={mobileMenuOpen} aria-controls="workspace-navigation" onClick={() => setMobileMenuOpen(true)}><Menu size={20}/></button><div><h1>{title}</h1><p>{new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' })}</p></div><div className="cf-header-actions"><Link to={isCPA ? '/messages' : '/my-messages'}><MessageSquare size={16}/>Messages</Link><Link to="/">ClearFile website<ArrowUpRight size={14}/></Link><span className="cf-avatar" title={profile?.full_name}>{initials}</span></div></header>
      <main id="workspace-main" tabIndex={-1} className="cf-workspace-main">{location.pathname === '/reminders' && <p className="cf-context-note">Requests & reminders · Ask clients for documents, organizer responses and other outstanding items.</p>}{location.pathname === '/calendar' && <p className="cf-context-note">Tasks & deadlines · Review upcoming filing deadlines and open the related client work.</p>}{children}</main>
    </div>
  </div>
}
